import React, { useRef } from "react";
import {
  Autocomplete,
  Box,
  Grid,
  ListItem,
  Paper,
  styled,
  Skeleton,
  Typography
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import TimeRangeSelector from "../../components/layouts/common/TimeRangeSelector";
import { findObjectKeyByValue, titleCase, formatNumberCustomPattern } from "../../utils/helper";
import { REGIONS_TYPE } from "../../utils/constants";
import { StyledSearchBar } from "../../components/inputs/SearchBar";
import AsyncDropDown from "../../components/inputs/AsyncDropDown";
import { getRegionApi } from "../../apis/region.api";
import { getProductApi } from "../../apis/product.api";
import Counts from "./Counts";
import { getCampApi, getCampByIdApi } from "../../apis/camp.api";
import ListController from "../beneficiary/ListController";
import { openModal } from "../../store/actions/modalAction";
import { HAS_HEARING_LOSS } from "../../utils/beneficiary.constant";
import { getCityApi, getCountryApi, getStateApi } from "../../apis/CountryStateCity.api";
import CustomInput from "../../components/inputs/CustomInput";

const FiltersBox = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const PaddingBoxInDesktop = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: theme.spacing(3),
  },
}));

const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const DashboardListUi = ({ filters, setFilters, list, loading }) => {
  const { user } = useSelector((state) => state);
  const dispatch = useDispatch();
  const value = useRef({
    country: "India",
    state: null,
    district: null,
  })
  const APICall = (type) => {
    switch (type) {
      case REGIONS_TYPE.COUNTRY:
        return {
          api: (para) => getCountryApi({ ...para, isName: true }),
          label: "Select Country",
          fields: "country"
        };
      case REGIONS_TYPE.STATE:
        return {
          api: (para) => getStateApi({ ...para, country: value.current?.country, isName: true }),
          label: "Select State",
          fields: "state"
        };
      case REGIONS_TYPE.DISTRICT:
        return {
          api: (para) => getCityApi({ ...para, state: value.current?.state, isName: true }),
          label: "Select City",
          fields: "district"
        };
      default:
        return null;
    }
  };

  return (
    <DashboardContainer mb={3} id="dashboard-content">
      <Box mt={3}>
        {/* Filters */}
        <Paper component={Box} p={2}>
          <FiltersBox id="filter-box">
            {/* Time Range */}
            <PaddingBoxInDesktop
              mb={1}
              mt={1}
              pr={3}
              sx={{ display: "flex", flex: 1, justifyContent: "flex-end", "*": { justifyContent: "center" } }}
            >
              <TimeRangeSelector onChange={(newRange) => { setFilters({ ...filters, ...newRange }) }} />
            </PaddingBoxInDesktop>

            {/* Region Type */}
            <PaddingBoxInDesktop mb={1} mt={1} sx={{ display: "flex", flex: 1, justifyContent: "flex-end" }}>
              <Autocomplete
                disableClearable
                value={filters.type ? findObjectKeyByValue(filters.type, REGIONS_TYPE) : null}
                onChange={(e, newVal) => {
                  setFilters({
                    ...filters,
                    type: newVal ? newVal._id : null,
                    country: null,
                    state: null,
                    district: null,
                    taluka: null,
                  });
                }}
                // options={[
                //   ...Object.keys(REGIONS_TYPE).map((key) => ({
                //     label: titleCase(key),
                //     _id: REGIONS_TYPE[key],
                //   })),
                // ]}
                options={Object.keys(REGIONS_TYPE)
                  .filter((key) => key !== "TALUKA")
                  .map((key) => ({
                    label: titleCase(key),
                    _id: REGIONS_TYPE[key],
                  }))}
                sx={{ width: "100%", display: "flex", "*": { display: "flex", justifyContent: "center", flex: 1 } }}
                renderInput={(params) => (
                  <StyledSearchBar placeholder="Select Region Type*" {...params} />
                )}
              />
            </PaddingBoxInDesktop>

            {/* Region Dropdown */}
            <PaddingBoxInDesktop
              // mb={2}
              mt={"12px"}
              sx={{ display: "flex", flex: 1, justifyContent: "flex-end", height:"5vh", alignItems:"center" }}
              pl={3}
            >
              <AsyncDropDown
                key={filters.type}
                Value={filters[APICall(filters.type)?.fields] ? { _id: 1, name: filters[APICall(filters.type)?.fields] } : null}
                lazyFun={async (para) => {
                  const apiFn = APICall(filters.type, filters);
                  return apiFn ? await apiFn.api(para) : [];
                }}
                OptionComponent={({ option, ...rest }) => (
                  <ListItem {...rest}>{option?.name}</ListItem>
                )}
                onChange={async (changedVal) => {
                  const key = APICall(filters.type)?.fields;
                  const updatedFilters = {
                    ...filters,
                    [key]: changedVal ? changedVal.name : null,
                  };

                  if (key === "country") {
                    updatedFilters.state = null;
                    updatedFilters.district = null;
                  } else if (key === "state") {
                    updatedFilters.district = null;
                  }

                  setFilters(updatedFilters);
                  value.current = { ...value.current, ...updatedFilters };
                }}
                titleKey={"name"}
                valueKey={"_id"}
                InputComponent={(params) => {
                  const label = APICall(filters.type)?.label || "";
                  return (
                    <StyledSearchBar
                      {...params}
                      label={`${label}*`}
                      placeholder={`${label}*`}
                      // margin="dense"
                      // InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              />
            </PaddingBoxInDesktop>

            {/* Camp */}
            <PaddingBoxInDesktop mb={1} mt={1} sx={{ display: "flex", flex: 1, justifyContent: "flex-end" }} pl={3}>
              <AsyncDropDown
                lazyFun={async (para) => await getCampApi({ ...para, allStatus: true })}
                OptionComponent={({ option, ...rest }) => <ListItem {...rest}>{option.name}</ListItem>}
                onChange={async (changedVal) => {
                  setFilters({ ...filters, camp: changedVal ? changedVal._id : null });
                }}
                titleKey={"name"}
                valueKey={"_id"}
                InputComponent={(params) => (
                  <StyledSearchBar placeholder={"Select Camp"} {...params} margin="none" />
                )}
              />
            </PaddingBoxInDesktop>

          </FiltersBox>
        </Paper>

        {/* Counts Section */}
        <Typography variant="h6" mt={3} mb={1}>
          Overall Data
        </Typography>
        <Grid container spacing={2}>
          {loading ? (
            [0, 1, 2, 3].map((i) => (
              <Grid item xs={12} md={3} key={i}>
                <Paper component={Box} p={2}>
                  <Skeleton variant="rounded" height="120px" width="100%" />
                </Paper>
              </Grid>
            ))
          ) : (
            <>
              <Grid item xs={12} md={2.4}>
                <Counts title="Total Camps" count={formatNumberCustomPattern(list.overall?.camp_count ?? 0)} />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Counts title="Total Beneficiaries" count={formatNumberCustomPattern(list.overall?.total ?? 0)} onClick={() =>
                  dispatch(
                    openModal(
                      <ListController defaultFilters={{ hearingLoss: null }} />,
                      "md",
                      false,
                    )
                  )
                } />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Counts title="Normal Hearing" count={formatNumberCustomPattern(list.overall?.normal ?? 0)}
                  onClick={() =>
                    dispatch(
                      openModal(
                        <ListController defaultFilters={{ hearingLoss: HAS_HEARING_LOSS.NO }} />,
                        "md",
                        false,
                      )
                    )
                  } />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Counts title="Hearing Loss" count={formatNumberCustomPattern(list.overall?.hearing_loss ?? 0)} onClick={() =>
                  dispatch(
                    openModal(
                      <ListController defaultFilters={{ hearingLoss: HAS_HEARING_LOSS.YES }} />,
                      "md",
                      false,
                    )
                  )
                } />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Counts title="Pending" count={formatNumberCustomPattern(list.overall?.not_tested ?? 0)} onClick={() =>
                  dispatch(
                    openModal(
                      <ListController defaultFilters={{ hearingLoss: 3 }} />,
                      "md",
                      false,
                    )
                  )
                } />
              </Grid>
            </>
          )}
        </Grid>

        {/* Daily Counts */}
        <Typography variant="h6" mt={4} mb={1}>
          Daily Data
        </Typography>
        <Grid container spacing={2}>
          {loading ? (
            [0, 1, 2, 3].map((i) => (
              <Grid item xs={12} md={3} key={i}>
                <Paper component={Box} p={2}>
                  <Skeleton variant="rounded" height="120px" width="100%" />
                </Paper>
              </Grid>
            ))
          ) : (
            <>
              <Grid item xs={12} md={3}>
                <Counts title=" Camps" count={formatNumberCustomPattern(list.daily?.camp_count ?? 0)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Counts title=" Beneficiaries" count={formatNumberCustomPattern(list.daily?.total ?? 0)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Counts title=" Normal Hearing" count={formatNumberCustomPattern(list.daily?.normal ?? 0)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Counts title=" Hearing Loss" count={formatNumberCustomPattern(list.daily?.hearing_loss ?? 0)} />
              </Grid>
            </>
          )}
        </Grid>

      </Box>
    </DashboardContainer>
  );
};

export default DashboardListUi;
